/**
 * JU!CE — Carrossel de UGC (sections/jp-ugc.liquid).
 *
 * Regra do componente no Figma (198:47): "Autoplay a cada 7 segundos apenas
 * enquanto nenhum vídeo estiver tocando; pausar no hover, no foco de teclado,
 * durante arrasto ou reprodução; mudo por padrão; suportar setas, swipe/drag,
 * navegação por teclado e prefers-reduced-motion."
 *
 * 11/09: o vídeo de cada card toca sozinho, mudo e em loop, enquanto o card está
 * na tela (IntersectionObserver a 60%); pausa fora da tela, com a aba oculta e em
 * prefers-reduced-motion. O botão da barra liga o som — de um card por vez.
 *
 * Progressive enhancement: sem JS o <ul> continua rolando no dedo, os links de
 * vídeo continuam clicáveis e os controles ficam escondidos (.js no <html>).
 */
(function () {
  'use strict';

  var MQ_REDUCE = '(prefers-reduced-motion: reduce)';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function prefersReducedMotion() {
    return window.matchMedia ? window.matchMedia(MQ_REDUCE).matches : false;
  }

  class JpUgcCarousel extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-jp-ugc-rail]');
      if (!this.rail) return;

      this.slides = toArray(this.querySelectorAll('[data-jp-ugc-slide]'));
      if (!this.slides.length) return;

      this.prevBtn = this.querySelector('[data-jp-ugc-prev]');
      this.nextBtn = this.querySelector('[data-jp-ugc-next]');
      this.currentEl = this.querySelector('[data-jp-ugc-current]');
      this.progressEl = this.querySelector('[data-jp-ugc-progress]');

      this.index = 0;
      this.paused = false;
      this.playing = false;
      this.visible = false;
      this.timer = 0;
      this.interval = (parseFloat(this.getAttribute('data-autoplay')) || 0) * 1000;

      this.bind();
      this.onScroll();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }
      if (this.rail && this._onScroll) {
        this.rail.removeEventListener('scroll', this._onScroll);
      }
    }

    bind() {
      var self = this;

      this._onScroll = this.throttleScroll();
      this.rail.addEventListener('scroll', this._onScroll, { passive: true });

      /* Autoplay só enquanto a seção está na tela: fora dela o relógio nem começa,
         e ao voltar recomeça do zero (senão o carrossel "pula" sozinho enquanto
         ninguém está vendo). */
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.visible = entry.isIntersecting;
            if (self.visible) { self.start(); } else { self.stop(); }
          });
        }, { threshold: 0.4 });
        this.observer.observe(this);
      } else {
        this.visible = true;
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); });

      /* Pausa: mouse, foco de teclado, arrasto e aba em segundo plano. */
      ['mouseenter', 'focusin', 'pointerdown', 'touchstart'].forEach(function (evt) {
        self.addEventListener(evt, function () { self.pause(); }, { passive: true });
      });
      ['mouseleave', 'focusout', 'pointerup', 'touchend'].forEach(function (evt) {
        self.addEventListener(evt, function () { self.resume(); }, { passive: true });
      });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { self.pause(); } else { self.resume(); }
      });

      /* Teclado no trilho. */
      this.rail.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') { event.preventDefault(); self.step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); self.step(-1); }
      });

      /* Vídeos: enquanto um toca, o carrossel não anda. */
      this.videos = toArray(this.querySelectorAll('video'));
      this.videos.forEach(function (video) {
        var card = video.closest('.jp-ugc-card');
        video.addEventListener('play', function () {
          self.playing = true;
          self.stop();
          if (card) card.classList.add('is-playing');
        });
        ['pause', 'ended'].forEach(function (evt) {
          video.addEventListener(evt, function () {
            self.playing = self.algumVideoTocando();
            if (card) card.classList.remove('is-playing');
            self.resume();
          });
        });
      });

      this.bindAutoplayDeVideo();
      this.bindBotoesDeSom();
    }

    algumVideoTocando() {
      if (!this.videos) return false;
      for (var i = 0; i < this.videos.length; i++) {
        if (!this.videos[i].paused && !this.videos[i].ended) return true;
      }
      return false;
    }

    tocar(video) {
      var played = video.play();
      if (played && typeof played.catch === 'function') played.catch(function () {});
    }

    /* Autoplay mudo do vídeo do card enquanto ele está na tela. */
    bindAutoplayDeVideo() {
      var self = this;
      if (!this.videos || !this.videos.length) return;

      this.videos.forEach(function (video) {
        video.muted = true;
        video.loop = true;
        video.setAttribute('playsinline', '');
      });

      if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            video.setAttribute('data-jp-ugc-inview', '');
            if (!document.hidden) self.tocar(video);
          } else {
            video.removeAttribute('data-jp-ugc-inview');
            video.pause();
          }
        });
      }, { threshold: 0.6 });

      this.slides.forEach(function (slide) {
        if (slide.querySelector('video')) self.videoObserver.observe(slide);
      });

      document.addEventListener('visibilitychange', function () {
        self.videos.forEach(function (video) {
          if (document.hidden) {
            video.pause();
          } else if (video.hasAttribute('data-jp-ugc-inview')) {
            self.tocar(video);
          }
        });
      });
    }

    /* Botão de som da barra: só um card com áudio por vez. */
    bindBotoesDeSom() {
      var self = this;
      this.muteBtns = toArray(this.querySelectorAll('[data-jp-ugc-mute]'));
      this.muteBtns.forEach(function (button) {
        button.addEventListener('click', function () {
          var card = button.closest('.jp-ugc-card');
          var video = card ? card.querySelector('video') : null;
          if (!video) return;
          var ligarSom = video.muted;
          if (ligarSom) {
            self.videos.forEach(function (outro) {
              if (outro !== video) self.definirSom(outro, true);
            });
          }
          self.definirSom(video, !ligarSom);
          if (ligarSom && video.paused) self.tocar(video);
        });
      });
    }

    definirSom(video, mudo) {
      video.muted = mudo;
      var card = video.closest('.jp-ugc-card');
      var button = card ? card.querySelector('[data-jp-ugc-mute]') : null;
      if (!button) return;
      button.setAttribute('aria-pressed', mudo ? 'true' : 'false');
      button.classList.toggle('is-unmuted', !mudo);
    }

    throttleScroll() {
      var self = this;
      var frame = 0;
      return function () {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = 0;
          self.onScroll();
        });
      };
    }

    onScroll() {
      var railLeft = this.rail.getBoundingClientRect().left;
      var closest = 0;
      var closestDistance = Infinity;

      for (var i = 0; i < this.slides.length; i++) {
        var distance = Math.abs(this.slides[i].getBoundingClientRect().left - railLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = i;
        }
      }

      /* 15/09 (revisão): com vários cards visíveis o trilho acaba antes do último índice,
         e o contador travava em "02". Nos extremos, o slide corrente é o primeiro/último. */
      var maxScroll = this.rail.scrollWidth - this.rail.clientWidth;
      if (maxScroll > 0 && this.rail.scrollLeft >= maxScroll - 2) {
        closest = this.slides.length - 1;
      } else if (this.rail.scrollLeft <= 2) {
        closest = 0;
      }

      this.index = closest;
      this.render();
    }

    render() {
      if (this.currentEl) {
        this.currentEl.textContent = pad(this.index + 1);
      }

      if (this.progressEl) {
        /* Figma 198:80/198:81: barra com 1/n da largura andando com o scroll. */
        var width = 1 / this.slides.length;
        var max = this.rail.scrollWidth - this.rail.clientWidth;
        var ratio = max > 0 ? Math.min(1, Math.max(0, this.rail.scrollLeft / max)) : 0;
        this.progressEl.style.width = (width * 100).toFixed(2) + '%';
        this.progressEl.style.transform = 'translateX(' + (ratio * (100 / width - 100)).toFixed(2) + '%)';
      }
    }

    goTo(index) {
      var slide = this.slides[index];
      if (!slide) return;
      this.rail.scrollTo({
        left: slide.offsetLeft - this.slides[0].offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    step(direction) {
      /* 15/09 (revisão): "Próximo vídeo" no fim do trilho não saía do lugar (clique morto).
         Nos limites as duas setas circulam — "Anterior" já fazia isso no início. */
      var limite = this.rail.scrollWidth - this.rail.clientWidth;
      if (direction > 0 && (limite <= 0 || this.rail.scrollLeft >= limite - 2)) {
        this.goTo(0);
        return;
      }
      if (direction < 0 && this.rail.scrollLeft <= 2) {
        this.goTo(this.slides.length - 1);
        return;
      }

      /* 17/09: com vários cards visíveis (desktop) o scroll do slide vizinho é limitado ao fim
         do trilho e "Anterior" no fim não saía do lugar. Anda até o slide que de fato move. */
      var next = this.index;
      for (var n = 0; n < this.slides.length; n++) {
        next += direction;
        if (next < 0) next = this.slides.length - 1;
        if (next > this.slides.length - 1) next = 0;
        var alvo = Math.min(limite, Math.max(0, this.slides[next].offsetLeft - this.slides[0].offsetLeft));
        if (Math.abs(alvo - this.rail.scrollLeft) > 2) break;
      }
      this.goTo(next);
    }

    start() {
      if (!this.interval || prefersReducedMotion() || this.paused || this.playing) return;
      if (!this.visible) return;
      this.stop();
      var self = this;
      this.timer = window.setInterval(function () { self.step(1); }, this.interval);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = 0;
      }
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.start();
    }
  }

  function boot() {
    if (!('customElements' in window)) return;
    if (!window.customElements.get('jp-ugc-carousel')) {
      window.customElements.define('jp-ugc-carousel', JpUgcCarousel);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
